import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FileText,
  Home,
  Menu,
  MessageSquare,
  Package,
  ReceiptText,
  Search,
  Tag,
  ShoppingBag,
  X,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import PageHero from "../components/PageHero";
import heroImage from "../assets/zeamays.png";
import {
  cancelAdminOrder,
  isAdminUser,
  loadAdminOrders,
  updateAdminOrderStatus,
} from "../services/admin";

const ORDER_STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "processing", label: "Processing" },
  { value: "ready_for_delivery", label: "Out for delivery" },
  { value: "delivered", label: "Delivered" },
  { value: "cancelled", label: "Cancelled" },
];

const navItems = [
  { label: "Dashboard", href: "/admin", icon: Home },
  { label: "Orders", href: "/admin/orders", icon: ReceiptText },
  { label: "Blog", href: "/admin/blog", icon: FileText },
  { label: "Products", href: "/admin/products", icon: Package },
  { label: "Reviews", href: "/admin/reviews", icon: MessageSquare },
  { label: "Promotions", href: "/admin/promotions", icon: Tag },
];

const statusStyles = {
  pending: "bg-amber-50 text-amber-700 ring-amber-100",
  processing: "bg-blue-50 text-blue-700 ring-blue-100",
  delivered: "bg-green-50 text-green-700 ring-green-100",
  cancelled: "bg-red-50 text-red-700 ring-red-100",
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    maximumFractionDigits: 0,
  }).format(Number(amount) || 0);

const formatDate = (dateValue) => {
  if (!dateValue) return "Not available";

  return new Intl.DateTimeFormat("en-NG", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(dateValue));
};

const getOrderReference = (order) => order.payment_reference || order.id || "No reference";
const getTrackingStatus = (order) => order.status;
const formatStatusLabel = (status) => {
  if (String(status || "").trim() === "ready_for_delivery") {
    return "Out for delivery";
  }

  return String(status || "pending").replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const normalizeCartItems = (items) => {
  if (!Array.isArray(items)) return [];

  return items.map((item, index) => ({
    id: item.id || item.product_id || `${item.product_name || "item"}-${index}`,
    product_name: item.product_name || item.name || "Unnamed product",
    selected_size: item.selected_size || item.selectedSize || item.option_label || item.optionLabel || "",
    unit_price: Number(item.unit_price ?? item.unitPrice ?? item.price) || 0,
    price: Number(item.unit_price ?? item.unitPrice ?? item.price) || 0,
    quantity: Number(item.quantity) || 1,
  }));
};

const getOrderProducts = (order) => {
  if (Array.isArray(order?.order_items) && order.order_items.length) {
    return normalizeCartItems(order.order_items);
  }

  return normalizeCartItems(order?.cart_items);
};

const readOrderField = (order, keys) => {
  for (const key of keys) {
    const value = order?.[key];
    if (value) return value;
  }

  if (Array.isArray(order?.cart_items)) {
    for (const item of order.cart_items) {
      for (const key of keys) {
        const value = item?.[key] || item?.customerDetails?.[key] || item?.deliveryInfo?.[key];
        if (value) return value;
      }
    }
  }

  return "";
};

const AdminOrders = () => {
  const { user, loading } = useAuth();
  const [authorized, setAuthorized] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [savingOrderId, setSavingOrderId] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadPage = async () => {
      if (!user) {
        if (!cancelled) {
          setAuthorized(false);
          setCheckingAccess(false);
        }
        return;
      }

      try {
        const adminAccess = await isAdminUser(user);
        if (!cancelled) {
          setAuthorized(adminAccess);
        }

        if (adminAccess) {
          const loadedOrders = await loadAdminOrders();
          if (!cancelled) {
            setOrders(loadedOrders);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Unable to load admin orders.");
        }
      } finally {
        if (!cancelled) {
          setCheckingAccess(false);
        }
      }
    };

    loadPage();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const filteredOrders = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return orders.filter((order) => {
      const trackingStatus = getTrackingStatus(order).toLowerCase();
      const matchesStatus = statusFilter === "all" || trackingStatus === statusFilter;
      const searchable = [
        getOrderReference(order),
        order.email,
        order.payment_status,
        trackingStatus,
        order.id,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return matchesStatus && (!query || searchable.includes(query));
    });
  }, [orders, searchTerm, statusFilter]);

  const replaceOrder = (updatedOrder) => {
    setOrders((currentOrders) =>
      currentOrders.map((order) => (order.id === updatedOrder.id ? updatedOrder : order))
    );
    setSelectedOrder((currentOrder) =>
      currentOrder?.id === updatedOrder.id ? updatedOrder : currentOrder
    );
  };

  const handleStatusChange = async (orderId, status) => {
    setSavingOrderId(orderId);
    setError("");

    try {
      const updatedOrder = await updateAdminOrderStatus(orderId, status);
      replaceOrder(updatedOrder);
    } catch (err) {
      setError(err.message || "Unable to update order status.");
    } finally {
      setSavingOrderId("");
    }
  };

  const handleCancelOrder = async (orderId) => {
    setSavingOrderId(orderId);
    setError("");

    try {
      const updatedOrder = await cancelAdminOrder(orderId);
      replaceOrder(updatedOrder);
    } catch (err) {
      setError(err.message || "Unable to cancel order.");
    } finally {
      setSavingOrderId("");
    }
  };

  if (loading || checkingAccess) {
    return (
      <div className="min-h-screen bg-lime-50 px-6 py-28 text-center text-slate-700">
        <p className="text-lg font-semibold">Checking admin access...</p>
      </div>
    );
  }

  if (!authorized) {
    return (
      <div className="min-h-screen bg-lime-50 px-6 py-28 text-center text-slate-700">
        <div className="mx-auto max-w-xl rounded-lg border border-red-200 bg-white p-8 shadow-sm">
          <h1 className="text-3xl font-semibold text-red-700">Access denied</h1>
          <p className="mt-4 text-lg">This page is only available to Zea Mays admin accounts.</p>
          <Link to="/" className="mt-6 inline-block rounded-md bg-red-700 px-5 py-3 text-white">
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7fbef] pt-16 text-slate-900">
      <div className="lg:flex">
        <aside className="fixed bottom-0 left-0 top-16 hidden w-64 border-r border-lime-200 bg-white lg:block">
          <AdminSidebar />
        </aside>

        <header className="sticky top-16 z-30 border-b border-lime-200 bg-white/95 px-4 py-3 backdrop-blur lg:hidden">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-red-700">Zea Mays</p>
              <h1 className="text-lg font-bold text-slate-950">Orders Management</h1>
            </div>
            <button
              type="button"
              onClick={() => setMobileNavOpen((current) => !current)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-lime-200 text-red-700"
              aria-label="Toggle admin navigation"
            >
              {mobileNavOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
          {mobileNavOpen ? (
            <nav className="mt-3 grid gap-2">
              {navItems.map((item) => (
                <AdminNavLink key={item.label} item={item} onClick={() => setMobileNavOpen(false)} />
              ))}
            </nav>
          ) : null}
        </header>

        <main className="w-full px-4 py-6 sm:px-6 lg:ml-64 lg:px-8 lg:py-8">
          <div className="mx-auto max-w-7xl">
            <div className="mb-8">
              <PageHero
                eyebrow="Order workspace"
                title="Keep every order moving with confidence."
                description="Search, inspect, and update order progress from a streamlined control view designed for the team."
                image={heroImage}
                imageAlt="Zea Mays order management"
                primaryAction={{ to: "/admin", label: "Back to Dashboard" }}
                secondaryAction={{ to: "/admin/products", label: "Products" }}
                stats={[
                  { label: "Visible", value: `${filteredOrders.length} orders` },
                  { label: "Total", value: `${orders.length} orders` },
                  { label: "Pending", value: `${orders.filter((order) => String(order.status || "").toLowerCase() === "pending").length}` },
                  { label: "Delivered", value: `${orders.filter((order) => String(order.status || "").toLowerCase() === "delivered").length}` },
                ]}
                compact
              />
            </div>

            {error ? (
              <div className="mb-6 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                {error}
              </div>
            ) : null}

            <section className="mb-6 grid gap-3 rounded-lg border border-lime-200 bg-white p-4 shadow-sm md:grid-cols-[1fr_220px]">
              <label className="relative block">
                <span className="sr-only">Search orders</span>
                <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search by reference, email, status, or order ID"
                  className="h-11 w-full rounded-md border border-lime-200 bg-lime-50 pl-10 pr-3 text-sm outline-none transition focus:border-red-600 focus:bg-white focus:ring-2 focus:ring-red-100"
                />
              </label>
              <label className="block">
                <span className="sr-only">Filter by status</span>
                <select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  className="h-11 w-full rounded-md border border-lime-200 bg-lime-50 px-3 text-sm font-semibold text-slate-700 outline-none transition focus:border-red-600 focus:bg-white focus:ring-2 focus:ring-red-100"
                >
                  <option value="all">All statuses</option>
                  {ORDER_STATUSES.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </label>
            </section>

            <section className="rounded-lg border border-lime-200 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-lime-200 px-5 py-4">
                <div>
                  <h3 className="text-xl font-bold text-slate-950">All Orders</h3>
                  <p className="text-sm text-slate-500">Connected to order and item records</p>
                </div>
                <ShoppingBag className="h-6 w-6 text-red-700" />
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-[1160px] w-full text-left text-sm">
                  <thead className="bg-lime-50 text-xs font-bold uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-5 py-3">Order Reference</th>
                      <th className="px-5 py-3">Customer Email</th>
                      <th className="px-5 py-3">Total Amount</th>
                      <th className="px-5 py-3">Payment Status</th>
                      <th className="px-5 py-3">Tracking Status</th>
                      <th className="px-5 py-3">Order Date</th>
                      <th className="px-5 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-lime-100">
                    {filteredOrders.length ? (
                      filteredOrders.map((order) => {
                        const trackingStatus = getTrackingStatus(order);
                        const disabled = savingOrderId === order.id;
                        const isCancelled = trackingStatus === "cancelled";

                        return (
                          <tr key={order.id} className="align-middle">
                            <td className="px-5 py-4 font-semibold text-slate-950">
                              {getOrderReference(order)}
                            </td>
                            <td className="px-5 py-4 text-slate-600">{order.email || "No email"}</td>
                            <td className="px-5 py-4 font-semibold text-slate-950">
                              {formatCurrency(order.total_amount)}
                            </td>
                            <td className="px-5 py-4">
                              <StatusPill status={order.payment_status || order.status || "pending"} />
                            </td>
                            <td className="px-5 py-4">
                              <select
                                value={trackingStatus}
                                onChange={(event) => handleStatusChange(order.id, event.target.value)}
                                disabled={disabled}
                                className="h-10 rounded-md border border-lime-200 bg-white px-3 text-sm font-semibold text-slate-700 outline-none transition focus:border-red-600 focus:ring-2 focus:ring-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                {ORDER_STATUSES.map((status) => (
                                  <option key={status.value} value={status.value}>
                                    {status.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="px-5 py-4 text-slate-600">{formatDate(order.created_at)}</td>
                            <td className="px-5 py-4">
                              <div className="flex justify-end gap-2">
                                {!isCancelled && trackingStatus !== "delivered" ? <button
                                  type="button"
                                  onClick={() => handleStatusChange(order.id, "ready_for_delivery")}
                                  disabled={disabled}
                                  className="rounded-md border border-blue-200 px-3 py-2 text-sm font-semibold text-blue-700 transition hover:bg-blue-50 disabled:opacity-60"
                                >Mark Ready</button> : null}
                                {!isCancelled && trackingStatus !== "delivered" ? <button
                                  type="button"
                                  onClick={() => handleStatusChange(order.id, "delivered")}
                                  disabled={disabled}
                                  className="rounded-md border border-green-200 px-3 py-2 text-sm font-semibold text-green-700 transition hover:bg-green-50 disabled:opacity-60"
                                >Mark Delivered</button> : null}
                                <button
                                  type="button"
                                  onClick={() => setSelectedOrder(order)}
                                  className="rounded-md border border-lime-200 px-3 py-2 text-sm font-semibold text-red-700 transition hover:bg-lime-50"
                                >
                                  View
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleCancelOrder(order.id)}
                                  disabled={disabled || isCancelled}
                                  className="rounded-md bg-red-700 px-3 py-2 text-sm font-semibold text-white transition hover:bg-red-800 disabled:cursor-not-allowed disabled:bg-slate-300"
                                >
                                  Cancel Order
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="7" className="px-5 py-10 text-center text-slate-500">
                          No matching orders found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </main>
      </div>

      {selectedOrder ? (
        <OrderDetailModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onStatusChange={handleStatusChange}
          onCancel={handleCancelOrder}
          saving={savingOrderId === selectedOrder.id}
        />
      ) : null}
    </div>
  );
};

const OrderDetailModal = ({ order, onClose, onStatusChange, onCancel, saving }) => {
  const products = getOrderProducts(order);
  const trackingStatus = getTrackingStatus(order);
  const fullName = readOrderField(order, ["full_name", "fullName", "customer_name", "name"]);
  const phone = readOrderField(order, ["phone", "phone_number"]);
  const address = readOrderField(order, ["address", "delivery_address"]);
  const deliveryState = readOrderField(order, ["delivery_state", "deliveryState", "selectedState"]);
  const deliveryLGA = readOrderField(order, ["delivery_lga", "deliveryLGA", "selectedLGA"]);
  const deliveryNote = readOrderField(order, ["note", "delivery_note", "deliveryNote"]);
  const deliveryProvider = readOrderField(order, ["delivery_provider", "deliveryProvider"]) || order.delivery_quote?.provider || "";
  const courier = readOrderField(order, ["courier"]) || order.delivery_quote?.courier || "";
  const shipmentId = readOrderField(order, ["shipment_id", "shipmentId"]);
  const trackingNumber = readOrderField(order, ["tracking_number", "trackingNumber"]);
  const trackingUrl = readOrderField(order, ["tracking_url", "trackingUrl"]);
  const deliveryStatus = readOrderField(order, ["delivery_status", "deliveryStatus"]);
  const deliveryEta = readOrderField(order, ["delivery_eta", "deliveryEta", "estimated_delivery_date"]);
  const quotedAt = readOrderField(order, ["quoted_at", "quotedAt"]);

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-slate-950/60 px-3 py-4 sm:items-center">
      <div className="max-h-[92vh] w-full max-w-5xl overflow-y-auto rounded-lg bg-white shadow-2xl">
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-lime-200 bg-white px-5 py-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-red-700">Order details</p>
            <h3 className="mt-1 text-xl font-bold text-slate-950">{getOrderReference(order)}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-lime-200 text-slate-600 transition hover:bg-lime-50"
            aria-label="Close order details"
          >
            <X size={20} />
          </button>
        </div>

        <div className="grid gap-4 p-5 lg:grid-cols-2">
          <DetailSection title="Customer information">
            <DetailRow label="Name" value={fullName || "Not captured"} />
            <DetailRow label="Email" value={order.email || "Not captured"} />
            <DetailRow label="Phone" value={phone || "Not captured"} />
            <DetailRow label="User ID" value={order.user_id || "Not available"} />
          </DetailSection>

          <DetailSection title="Delivery information">
            <DetailRow label="Address" value={address || "Not captured"} />
            <DetailRow label="State" value={deliveryState || "Not captured"} />
            <DetailRow label="LGA" value={deliveryLGA || "Not captured"} />
            <DetailRow label="Note" value={deliveryNote || "None"} />
            <DetailRow label="Provider" value={deliveryProvider || "Not booked"} />
            <DetailRow label="Courier" value={courier || "Not booked"} />
            <DetailRow label="Shipment ID" value={shipmentId || "Not available"} />
            <DetailRow label="Tracking number" value={trackingNumber || "Not available"} />
            <DetailRow label="Shipment status" value={deliveryStatus || "Not booked"} />
            <DetailRow label="Estimated delivery" value={formatDate(deliveryEta)} />
            <DetailRow label="Quoted at" value={formatDate(quotedAt)} />
            {trackingUrl ? (
              <DetailRow
                label="Tracking URL"
                value={(
                  <a
                    href={trackingUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold text-amber-700 underline decoration-amber-300 underline-offset-2"
                  >
                    Open tracking page
                  </a>
                )}
              />
            ) : null}
          </DetailSection>

          <DetailSection title="Tracking information">
            <div className="mb-3">
              <StatusPill status={trackingStatus} />
            </div>
            <DetailRow label="Payment status" value={order.payment_status || order.status || "pending"} />
            <DetailRow label="Confirmed at" value={formatDate(order.payment_confirmed_at)} />
            <DetailRow label="Estimated delivery" value={formatDate(order.estimated_delivery_date)} />
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <select
                value={trackingStatus}
                onChange={(event) => onStatusChange(order.id, event.target.value)}
                disabled={saving}
                className="h-11 rounded-md border border-lime-200 bg-white px-3 text-sm font-semibold text-slate-700 outline-none transition focus:border-red-600 focus:ring-2 focus:ring-red-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {ORDER_STATUSES.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => onCancel(order.id)}
                disabled={saving || trackingStatus === "cancelled"}
                className="h-11 rounded-md bg-red-700 px-4 text-sm font-semibold text-white transition hover:bg-red-800 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                Cancel order
              </button>
            </div>
          </DetailSection>

          <DetailSection title="Payment summary">
            <DetailRow label="Payment reference" value={order.payment_reference || "Not available"} />
            <DetailRow label="Order date" value={formatDate(order.created_at)} />
            <DetailRow label="Total amount" value={formatCurrency(order.total_amount)} strong />
          </DetailSection>

          <DetailSection title="Status history">
            {(order.status_history || []).length ? (
              <ol className="space-y-2">
                {(order.status_history || []).slice().reverse().map((entry) => (
                  <li key={entry.id || `${entry.status}-${entry.created_at}`} className="flex items-center justify-between gap-3 rounded-md bg-white px-3 py-2 text-sm">
                    <span className="font-semibold text-slate-800">{formatStatusLabel(entry.status)}</span>
                    <time className="text-slate-500">{formatDate(entry.created_at)}</time>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-sm text-slate-500">No status changes recorded yet.</p>
            )}
          </DetailSection>
        </div>

        <div className="border-t border-lime-200 p-5">
          <h4 className="text-lg font-bold text-slate-950">Ordered products</h4>
          <div className="mt-3 overflow-x-auto rounded-lg border border-lime-200">
            <table className="min-w-[640px] w-full text-left text-sm">
              <thead className="bg-lime-50 text-xs font-bold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3">Size</th>
                  <th className="px-4 py-3">Quantity</th>
                  <th className="px-4 py-3">Unit price</th>
                  <th className="px-4 py-3 text-right">Subtotal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-lime-100">
                {products.length ? (
                  products.map((product) => (
                    <tr key={product.id}>
                      <td className="px-4 py-3 font-semibold text-slate-950">{product.product_name}</td>
                      <td className="px-4 py-3 text-slate-600">{product.selected_size || "Standard"}</td>
                      <td className="px-4 py-3 text-slate-600">{product.quantity}</td>
                      <td className="px-4 py-3 text-slate-600">{formatCurrency(product.unit_price)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-950">
                        {formatCurrency(product.price * product.quantity)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-slate-500">
                      No products found for this order.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const DetailSection = ({ title, children }) => (
  <section className="rounded-lg border border-lime-200 bg-lime-50/50 p-4">
    <h4 className="mb-3 text-base font-bold text-slate-950">{title}</h4>
    <div className="space-y-2">{children}</div>
  </section>
);

const DetailRow = ({ label, value, strong = false }) => (
  <div className="grid gap-1 sm:grid-cols-[150px_1fr]">
    <dt className="text-sm font-semibold text-slate-500">{label}</dt>
    <dd className={`break-words text-sm ${strong ? "font-bold text-slate-950" : "text-slate-700"}`}>
      {value}
    </dd>
  </div>
);

const AdminSidebar = () => (
  <div className="flex h-full flex-col px-5 py-6">
    <div className="border-b border-lime-200 pb-6">
      <p className="text-xs font-bold uppercase tracking-[0.28em] text-red-700">Zea Mays</p>
      <h2 className="mt-2 text-2xl font-bold text-slate-950">Admin</h2>
    </div>
    <nav className="mt-6 grid gap-2">
      {navItems.map((item) => (
        <AdminNavLink key={item.label} item={item} />
      ))}
    </nav>
  </div>
);

const AdminNavLink = ({ item, onClick }) => {
  const Icon = item.icon;

  return (
    <Link
      to={item.href}
      onClick={onClick}
      className="flex items-center gap-3 rounded-md px-3 py-3 text-sm font-semibold text-slate-700 transition hover:bg-lime-100 hover:text-red-700"
    >
      <Icon size={18} />
      {item.label}
    </Link>
  );
};

const StatusPill = ({ status }) => {
  const normalized = String(status || "pending").toLowerCase();
  const label =
    ORDER_STATUSES.find((item) => item.value === normalized)?.label || normalized.replace(/_/g, " ");

  return (
    <span
      className={`inline-flex rounded-full px-3 py-1 text-xs font-bold capitalize ring-1 ${
        statusStyles[normalized] || "bg-slate-50 text-slate-700 ring-slate-100"
      }`}
    >
      {label}
    </span>
  );
};

export default AdminOrders;
